import {useCallback, useMemo} from 'react'
import {Accordion, AccordionSummary, AccordionDetails, Typography, Box} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ViewInArIcon from '@mui/icons-material/ViewInAr'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {
  setDeviceWidth,
  setDeviceHeight,
  setDeviceDepth,
  setDeviceCornerRadius,
  setDeviceEdgeBevel,
  selectActiveScene,
  selectMinDeviceDimensions,
} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {setPreviewDimensions, clearPreviewDimensions} from '../../../common/components/deviceMesh/previewDimensions'
import SliderRow from '../sharedWidgets/SliderRow'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── Dimension slider range limits ─────────────────────────────────

const DIMENSION_RANGES = {
  // Maxes cover tablets too (iPad Pro 13" ≈ 215 × 281 mm), not just phones.
  width: {min: 30, max: 300, step: 0.1},
  height: {min: 60, max: 350, step: 0.1},
  depth: {min: 3, max: 15, step: 0.1},
  cornerRadius: {min: 0, max: 20, step: 0.1},
  edgeBevel: {min: 0, max: 5, step: 0.1},
} as const

// ── DeviceShapesPanel — shapes sub-accordion ──────────────────────

const DeviceShapesPanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()

  const deviceWidth = useAppSelector((s) => selectActiveScene(s).deviceWidth)
  const deviceHeight = useAppSelector((s) => selectActiveScene(s).deviceHeight)
  const deviceDepth = useAppSelector((s) => selectActiveScene(s).deviceDepth)
  const deviceCornerRadius = useAppSelector((s) => selectActiveScene(s).deviceCornerRadius)
  const deviceEdgeBevel = useAppSelector((s) => selectActiveScene(s).deviceEdgeBevel)
  const {minWidth, minHeight} = useAppSelector(selectMinDeviceDimensions)

  // Live handlers — write to preview store during drag (bypasses Redux/undo)
  const handleWidthChange = useCallback(
    (v: number) =>
      setPreviewDimensions({
        width: v,
        height: deviceHeight,
        depth: deviceDepth,
        cornerRadius: deviceCornerRadius,
        edgeBevel: deviceEdgeBevel,
      }),
    [deviceHeight, deviceDepth, deviceCornerRadius, deviceEdgeBevel],
  )
  const handleHeightChange = useCallback(
    (v: number) =>
      setPreviewDimensions({
        width: deviceWidth,
        height: v,
        depth: deviceDepth,
        cornerRadius: deviceCornerRadius,
        edgeBevel: deviceEdgeBevel,
      }),
    [deviceWidth, deviceDepth, deviceCornerRadius, deviceEdgeBevel],
  )
  const handleDepthChange = useCallback(
    (v: number) =>
      setPreviewDimensions({
        width: deviceWidth,
        height: deviceHeight,
        depth: v,
        cornerRadius: deviceCornerRadius,
        edgeBevel: deviceEdgeBevel,
      }),
    [deviceWidth, deviceHeight, deviceCornerRadius, deviceEdgeBevel],
  )
  const handleCornerRadiusChange = useCallback(
    (v: number) =>
      setPreviewDimensions({
        width: deviceWidth,
        height: deviceHeight,
        depth: deviceDepth,
        cornerRadius: v,
        edgeBevel: deviceEdgeBevel,
      }),
    [deviceWidth, deviceHeight, deviceDepth, deviceEdgeBevel],
  )
  const handleEdgeBevelChange = useCallback(
    (v: number) =>
      setPreviewDimensions({
        width: deviceWidth,
        height: deviceHeight,
        depth: deviceDepth,
        cornerRadius: deviceCornerRadius,
        edgeBevel: v,
      }),
    [deviceWidth, deviceHeight, deviceDepth, deviceCornerRadius],
  )

  // Committed handlers — dispatch to Redux (creates undo entry), worker fires on config change
  const handleWidthCommit = useCallback(
    (v: number) => {
      clearPreviewDimensions()
      dispatch(setDeviceWidth(v))
      dispatch(sealUndo())
    },
    [dispatch],
  )
  const handleHeightCommit = useCallback(
    (v: number) => {
      clearPreviewDimensions()
      dispatch(setDeviceHeight(v))
      dispatch(sealUndo())
    },
    [dispatch],
  )
  const handleDepthCommit = useCallback(
    (v: number) => {
      clearPreviewDimensions()
      dispatch(setDeviceDepth(v))
      dispatch(sealUndo())
    },
    [dispatch],
  )
  const handleCornerRadiusCommit = useCallback(
    (v: number) => {
      clearPreviewDimensions()
      dispatch(setDeviceCornerRadius(v))
      dispatch(sealUndo())
    },
    [dispatch],
  )
  const handleEdgeBevelCommit = useCallback(
    (v: number) => {
      clearPreviewDimensions()
      dispatch(setDeviceEdgeBevel(v))
      dispatch(sealUndo())
    },
    [dispatch],
  )

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Accordion disableGutters square sx={sx.subAccordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={sx.expandIcon} />} sx={sx.subSummary}>
        <ViewInArIcon sx={sx.subIcon} />
        <Typography sx={sx.subTitle}>{t('device.shapes.title')}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={sx.subDetails}>
        <Box sx={sx.groupCard}>
          <SliderRow
            label={t('device.shapes.width')}
            value={deviceWidth}
            min={minWidth}
            max={DIMENSION_RANGES.width.max}
            step={DIMENSION_RANGES.width.step}
            unit={t('device.shapes.unit')}
            onChange={handleWidthChange}
            onChangeCommitted={handleWidthCommit}
          />
          <SliderRow
            label={t('device.shapes.height')}
            value={deviceHeight}
            min={minHeight}
            max={DIMENSION_RANGES.height.max}
            step={DIMENSION_RANGES.height.step}
            unit={t('device.shapes.unit')}
            onChange={handleHeightChange}
            onChangeCommitted={handleHeightCommit}
          />
          <SliderRow
            label={t('device.shapes.depth')}
            value={deviceDepth}
            {...DIMENSION_RANGES.depth}
            unit={t('device.shapes.unit')}
            onChange={handleDepthChange}
            onChangeCommitted={handleDepthCommit}
          />
          <SliderRow
            label={t('device.shapes.cornerRadius')}
            value={deviceCornerRadius}
            {...DIMENSION_RANGES.cornerRadius}
            unit={t('device.shapes.unit')}
            onChange={handleCornerRadiusChange}
            onChangeCommitted={handleCornerRadiusCommit}
          />
          <SliderRow
            label={t('device.shapes.edgeBevel')}
            value={deviceEdgeBevel}
            {...DIMENSION_RANGES.edgeBevel}
            unit={t('device.shapes.unit')}
            onChange={handleEdgeBevelChange}
            onChangeCommitted={handleEdgeBevelCommit}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

// ── Styles — outer sub-accordion ──────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  subAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  subSummary: {
    minHeight: 40,
    padding: '0 4px',
    '&.Mui-expanded': {minHeight: 40},
    '& .MuiAccordionSummary-content': {
      margin: '8px 0',
      alignItems: 'center',
      gap: '6px',
      '&.Mui-expanded': {margin: '8px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 18,
  },
  subIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 16,
  },
  subTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.text.secondary,
  },
  subDetails: {
    padding: '0 4px 8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  groupCard: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '6px',
    padding: '8px',
    borderRadius: '6px',
    border: `1px solid ${tokens.accordion.border}`,
  },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: tokens.text.muted,
    marginBottom: '4px',
  },
})

export default DeviceShapesPanel
